/**
 * Page-context lines that tell Scout which exact skill the user picked from the
 * `/` menu, so a slug several skills share still activates the one they chose.
 * A typed `/slug` with no pick still works: Scout activates the most specific
 * skill with that slug.
 *
 * The API redacts UUIDs from page context for some plans; it keeps lines this
 * module formats, because a skill id points at a playbook, not a customer record.
 */

import { SKILL_ID_PATTERN, SKILL_SLUG_PATTERN } from "./slug";

export type SkillPick = { slug: string; id: string };

const SKILL_PICK_LINE = new RegExp(
  `^Skill picked from the / menu: /${SKILL_SLUG_PATTERN} is skill id ${SKILL_ID_PATTERN}\\.$`
);

const SKILL_PICK_INSTRUCTION = "Activate those skills with skill_activate using the skill id.";

function skillPickLine(pick: SkillPick): string {
  return `Skill picked from the / menu: /${pick.slug} is skill id ${pick.id}.`;
}

/** One line per pick, plus how to use it; undefined when there are none. */
export function formatSkillPickContext(picks: readonly SkillPick[]): string | undefined {
  const lines = picks.map(skillPickLine).filter(isSkillPickLine);
  if (lines.length === 0) {
    return undefined;
  }
  return [...lines, SKILL_PICK_INSTRUCTION].join("\n");
}

/** True only for a line exactly as formatSkillPickContext writes it. */
export function isSkillPickLine(line: string): boolean {
  return SKILL_PICK_LINE.test(line.trim());
}
